import { IconAdd, TextareaAction, Skeleton, Columns, Column } from '@machobear/design-system'

import { useTranslation } from 'react-i18next'
import { useLayoutEffect, useRef } from 'react'
import { TextArea } from '@/components/ds-form'
import useGetAutoReplyVariables from '@/hooks/use-get-auto-reply-variables'
import PreviewMessage from './PreviewMessage'
import { findBracketedWords, variablesDropdown } from '../helpers/utils'
import { AutoReplyVariable } from '../helpers/types'

export type MessageEditorProps = {
  onChange: (message: string) => void
  message: string
  isLoading: boolean
}

const MessageEditor: React.FC<MessageEditorProps> = ({
  message,
  onChange,
  isLoading
}: MessageEditorProps) => {
  const { t } = useTranslation()
  const textAreaRef = useRef<HTMLTextAreaElement | null>(null)
  const cursorPositionRef = useRef<number>()
  const { data = {}, isLoading: isVariablesLoading } = useGetAutoReplyVariables()
  const optionsDropdown = variablesDropdown(data)
  const highlightedRanges = findBracketedWords(
    message,
    Object.values(data).map((item: AutoReplyVariable) => item?.variable)
  )
  const cursorCaret = cursorPositionRef.current
  const handleCursorPosition = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    cursorPositionRef.current = e.target.selectionStart
  }
  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const refCursorPosition = textAreaRef.current?.selectionStart
    if (e.key === 'Backspace' && refCursorPosition !== undefined) {
      const rangeToDelete = highlightedRanges?.find(range => refCursorPosition === range.end)
      if (rangeToDelete) {
        e.preventDefault()
        const updatedMessage =
          message.slice(0, rangeToDelete.start) + message.slice(rangeToDelete.end)
        onChange(updatedMessage)
        cursorPositionRef.current = rangeToDelete.start
      }
    }
  }
  useLayoutEffect(() => {
    if (cursorPositionRef.current !== undefined && textAreaRef.current) {
      textAreaRef.current.setSelectionRange(cursorPositionRef.current, cursorPositionRef.current)
      textAreaRef.current.focus()
    }
  }, [message])
  return (
    <>
      {isVariablesLoading || isLoading ? (
        <Columns space="gutter" collapseBelow="desktop">
          <Column width="1/2">
            <Skeleton.Shape height={300} width={'100%'} />
          </Column>
          <Column width="1/2">
            <Skeleton.Shape height={300} width={'100%'} />
          </Column>
        </Columns>
      ) : (
        <Columns space="gutter" collapseBelow="desktop">
          <Column width="1/2">
            <TextArea
              ref={textAreaRef}
              name="auto_reply_message"
              label={t('l.settings.view.auto_reply.message', 'Message')}
              lines={14}
              required
              highlightRanges={highlightedRanges}
              onFocus={handleCursorPosition}
              onBlur={handleCursorPosition}
              onChange={handleCursorPosition}
              onKeyDown={handleKeyDown}
              actions={[
                <TextareaAction.Dropdown
                  label={t(
                    'l.settings.view.auto_reply.auto_reply_insert_variable',
                    'Insert Variable'
                  )}
                  icon={<IconAdd />}
                  options={optionsDropdown}
                  onChange={obj => {
                    const input = textAreaRef.current

                    if (input) {
                      const start = cursorCaret ?? message.length
                      if (obj) {
                        onChange(message.slice(0, start) + obj.value + message.slice(start))
                        cursorPositionRef.current = start + obj.value.length
                      }
                    }
                  }}
                />
              ]}
              characterLimit={4000}
              lineLimit={19}
            />
          </Column>
          <Column width="1/2">
            <PreviewMessage data={data} />
          </Column>
        </Columns>
      )}
    </>
  )
}

export default MessageEditor
